<template>
  <v-flex xs12>
    <page-toolbar>
      <period-control v-model="periodId" @change="periodChange" />
      <kendo-dropdownlist
        :data-source="marketDataSource"
        :data-text-field="'market'"
        :data-value-field="'marketId'"
        v-model="marketId"
        :autoBind="false"
        @databound="marketDataBound"
        @change="marketChange"
        style="width: 100px;"
      >
      </kendo-dropdownlist>
      <kendo-button
        icon="play"
        title="Run processes"
        @click="run"
        v-show="editable"
        v-if="selectedProcessIds.length"
      />
    </page-toolbar>
    <kendo-grid
      :columns="columns"
      :data-source="dataSource"
      :auto-bind="false"
      :detail-init="detailInit"
      :data-bound="dataBound"
      :autoBind="false"
      ref="grid"
    ></kendo-grid>
    <process-parameters
      :process-ids="selectedProcessIds"
      :selected-date="periodEndDate"
      :period-id="periodId"
      v-model="showRunner"
      v-if="showRunner"
      @batchCompleted="showRunner = false"
      @processCompleted="dataSource.read()"
      @close="showRunner = false"
    />
  </v-flex>
</template>

<script>
import PeriodControl from '@/components/PeriodControl'
import PageToolbar from '@/components/PageToolbar'
import {mapGetters} from 'vuex'
import notification from '../../helpers/notification'
import ProcessParameters from './ProcessParameters'

export default {
  name: 'process-manager',
  mounted() {
    this.$el.addEventListener('click', this.handleSelection, false)
  },
  beforeDestroy() {
    this.$el.addEventListener('click', this.handleSelection, false)
  },
  methods: {
    marketDataBound(e) {
      e.sender.select(0)
      this.marketId = e.sender.value()
      e.sender.trigger('change')
    },
    marketChange(e) {
      this.marketId = e.sender.value()
      this.dataSource.read()
    },
    run() {
      this.showRunner = true
    },
    periodChange(e) {
      this.periodEndDate = e.endDate
      if (!this.bound) {
        this.bound = true
        this.marketDataSource.read()
      }
    },
    handleSelection(e) {
      if (e.target.tagName != 'input' && e.target.type != 'checkbox') return

      const grid = this.$refs.grid.kendoWidget()
      this.selectedProcessIds = grid.selectedKeyNames().map(id => +id)
    },
    detailInit(e) {
      kendo
        .jQuery('<div/>')
        .appendTo(e.detailCell)
        .kendoGrid({
          dataSource: {
            type: 'aspnetmvc-ajax',
            transport: {
              read: {
                type: 'GET',
                url: '/api/process/ProcessManager/gridDetailSource',
                data: {importLogId: e.data.importLogId},
              },
            },
            schema: {
              data: 'data',
              total: 'total',
              model: {
                id: 'importLogDetailId',
                fields: {
                  importLogDetailId: {type: 'number'},
                  message: {type: 'string'},
                  logTypeName: {type: 'string'},
                },
              },
            },
            pageSize: 10,
            serverPaging: true,
            error: function(err) {
              notification.display(err.description, 'error')
            },
          },
          scrollable: false,
          sortable: false,
          pageable: true,
          columns: [
            {field: 'message', title: 'Comment', width: '500px'},
            {field: 'logTypeName', title: 'Log Type', width: '100px'},
          ],
        })
    },
  },
  data() {
    return {
      bound: false,
      marketId: -1,
      periodId: null,
      periodEndDate: null,
      selectedProcessIds: [],
      showRunner: false,
      marketDataSource: new kendo.data.DataSource({
        transport: {
          read: {
            url: '/api/process/ProcessManager/getUserMarkets',
            contentType: 'application/json',
            type: 'GET',
            data: () => ({
              clientId: this.clientId,
            }),
          },
        },
      }),
      dataSource: new kendo.data.DataSource({
        type: 'aspnetmvc-ajax',
        serverPaging: true,
        serverSorting: true,
        serverFiltering: true,
        transport: {
          read: {
            url: '/api/process/ProcessManager',
            type: 'GET',
            data: () => ({
              periodId: this.periodId,
              clientId: this.clientId,
              marketId: this.marketId,
            }),
            cache: false,
          },
        },
        schema: {
          errors: 'errors',
          total: 'total',
          data: 'data',
          model: {
            id: 'processId',
            fields: {
              processId: {type: 'number'},
              importLogId: {type: 'number'},
              isChecked: {type: 'boolean'},
              processName: {type: 'string'},
              processOrder: {type: 'number'},
              processDate: {type: 'date'},
              runDate: {type: 'date'},
              user: {type: 'string'},
              numberOfRecords: {type: 'number'},
              numberOfLogs: {type: 'number'},
            },
          },
        },
        error: function(err) {
          notification(err.description, 'error')
        },
      }),
      dataBound: function(e) {
        if (this.selectedProcessIds.length == 0) return

        const {sender} = e
        const data = this.dataSource.data()
        const toSelect = data.filter(item => this.selectedProcessIds.includes(item.processId))
        toSelect.forEach(item => sender.select(`tr[data-uid=${item.uid}]`))
      }.bind(this),
      columns: [
        {selectable: true, width: 35, attributes: {style: 'text-align: center;'}},
        {
          field: 'processName',
          title: 'Process Name',
          width: 250,
          headerAttributes: {style: 'text-align: center;'},
          attributes: {style: 'text-align: left;'},
        },
        {
          field: 'processDate',
          title: 'As Of Date',
          width: 100,
          format: '{0:yyyy-MM-dd}',
          attributes: {style: 'text-align: right;'},
          headerAttributes: {style: 'text-align: center;'},
        },
        {
          field: 'runDate',
          title: 'Last Process Date',
          width: 200,
          format: '{0:yyyy-MM-dd HH:mm}',
          attributes: {style: 'text-align: right;'},
          headerAttributes: {style: 'text-align: center;'},
        },
        {
          field: 'user',
          title: 'User',
          width: 150,
          headerAttributes: {style: 'text-align: center;'},
          attributes: {style: 'text-align: left;'},
        },
        {
          field: 'numberOfRecords',
          title: 'Records',
          width: 100,
          format: '{0:N0}',
          attributes: {style: 'text-align: right;'},
          headerAttributes: {style: 'text-align: center;'},
        },
        {
          field: 'numberOfLogs',
          title: 'Logs',
          width: 80,
          format: '{0:N0}',
          attributes: {style: 'text-align: right;'},
          headerAttributes: {style: 'text-align: center;'},
        },
      ],
    }
  },
  computed: {
    ...mapGetters({role: 'authentication/currentRole'}),
    ...mapGetters({clientId: 'authentication/selectedClientId'}),
    editable() {
      return ['SuperAdmin', 'Admin', 'SuperUser'].includes(this.role)
    },
  },
  components: {
    PeriodControl,
    PageToolbar,
    ProcessParameters,
  },
}
</script>
